/**
 * NewsSettingsDialog — Newsfeed-Einstellungen + Kategorien-Verwaltung mit Drag&Drop.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  MessageSquare, CheckCircle2, Pin, Plus, GripVertical, Pencil, Trash2,
} from 'lucide-react';
import api from '../../lib/api';

const SETTING_ROWS = [
  {
    key: 'comments_enabled',
    icon: MessageSquare,
    title: 'Kommentare',
    desc: 'Mitarbeiter können Beiträge kommentieren',
  },
  {
    key: 'confirmations_enabled',
    icon: CheckCircle2,
    title: 'Lesebestätigungen',
    desc: 'Mitarbeiter können Kenntnisnahme bestätigen',
  },
  {
    key: 'pinning_enabled',
    icon: Pin,
    title: 'Anpinnen',
    desc: 'Wichtige Beiträge oben anpinnen',
  },
];

export const NewsSettingsDialog = ({
  open, onOpenChange,
  settings, setSettings, onSave,
  categories, setCategories,
  onOpenCategoryDialog, onDeleteCategory,
}) => {
  const handleDrop = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const reordered = [...categories];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setCategories(reordered);
    try {
      await api.put('/news/categories/reorder', reordered.map((c) => c.id));
    } catch (err) {
      console.error('Failed to persist newsfeed category order:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Newsfeed-Einstellungen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {SETTING_ROWS.map((row) => {
            const Icon = row.icon;
            return (
              <label key={row.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg cursor-pointer">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{row.title}</p>
                    <p className="text-xs text-muted-foreground">{row.desc}</p>
                  </div>
                </div>
                <Checkbox
                  checked={settings[row.key]}
                  onCheckedChange={(c) => setSettings({ ...settings, [row.key]: c })}
                />
              </label>
            );
          })}

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">Kategorien</h4>
              <Button size="sm" variant="outline" onClick={() => onOpenCategoryDialog()}
                data-testid="btn-add-category">
                <Plus className="h-3 w-3 mr-1" /> Neu
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Reihenfolge per Drag &amp; Drop ändern</p>
            <div className="space-y-1.5" data-testid="category-list">
              {categories.map((cat, idx) => (
                <div
                  key={cat.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', idx.toString());
                    e.currentTarget.classList.add('opacity-50');
                  }}
                  onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('ring-2', 'ring-primary');
                  }}
                  onDragLeave={(e) =>
                    e.currentTarget.classList.remove('ring-2', 'ring-primary')
                  }
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('ring-2', 'ring-primary');
                    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                    handleDrop(fromIdx, idx);
                  }}
                  className="flex items-center justify-between p-2 rounded-lg border cursor-grab active:cursor-grabbing transition-all"
                  data-testid={`category-item-${cat.id}`}
                >
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <span className="text-sm">{cat.name}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => onOpenCategoryDialog(cat)}
                      data-testid={`edit-category-${cat.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => onDeleteCategory(cat.id)}
                      data-testid={`delete-category-${cat.id}`}>
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button onClick={onSave}>Speichern</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
